<script setup>
import WrTechnologiesFrameworkSimilarHeroSection from "~/components/global/WrTechnologiesFrameworkSimilarHeroSection.vue";
import { useGlobalStore } from "~/stores/globalStore";
import WrVideoHeroSection from "~/components/global/WrVideoHeroSection.vue";
import ServiceValueCard from "~/components/global/ServiceValueCard.vue";
import WrImageAndCardSection from "~/components/global/WrImageAndCardSection.vue";
const globalStore = useGlobalStore();
const config = useRuntimeConfig();
const baseURL = config.public.baseUrl;

const data = await import(
  "../../../public/content/services/frameworks/express-development-services-content.json"
);

const { isMobile } = useDevice();

useHead({
  title: data?.meta_tags.title || "",
  meta: data?.meta_tags.meta || [],
  link: [{ rel: "preload", as: "image", href: data?.header.image }],
});

const projects = ref([
  {
    id: "01",
    title: "Real time",
    tech: "PHP, Laravel, Vue, Tailwind",
  },
  {
    id: "02",
    title: "Order Management",
    tech: "PHP, Laravel, Vue, Tailwind",
  },
  {
    id: "03",
    title: "Analytic",
    tech: "PHP, Laravel, Vue, Tailwind",
  },
  {
    id: "04",
    title: "Leaning Management System",
    tech: "PHP, Laravel, Vue, Tailwind",
  },
  {
    id: "05",
    title: "Live Streaming",
    tech: "PHP, Laravel, Vue, Tailwind",
  },
]);
const visible = ref(false);

const formPrefilledData = ref(null);

const openModal = (prefilledValue = null) => {
  if (prefilledValue == null) formPrefilledData.value = null;
  else if (prefilledValue.length) formPrefilledData.value = prefilledValue;
  visible.value = true;
};
</script>

<template>
  <section class="dark:bg-none pb-10 lg:pb-20">
    <div class="container-wrapper-xl">
      <WrTechnologiesFrameworkSimilarHeroSection
        :data="data?.header"
        @open-modal="openModal"
      />
    </div>
  </section>

  <section class="dark:bg-none section-spacing">
    <div class="container-wrapper-xl">
      <WrVideoHeroSection :data="data?.our_promises" />
    </div>
  </section>

  <section class="dark:bg-none section-spacing">
    <div class="container-wrapper-xl">
      <h2 class="lg:text-center">
        <WrGradientText>
          {{ data?.our_services?.heading }}
        </WrGradientText>
      </h2>
      <p
        class="max-w-screen-lg mx-auto lg:text-center text-xl tracking-[0.5px]"
      >
        {{ data?.our_services?.subheading }}
      </p>

      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
        <div v-for="service in data?.our_services?.expertisetext">
          <ServiceValueCard
            :img="service?.image"
            :title="service?.heading"
            :description="service?.text"
          />
        </div>
      </div>
    </div>
  </section>

  <section class="dark:bg-none section-spacing">
    <div class="container-wrapper-xl">
      <WrImageListSection :data="data?.development_team_content" />
    </div>
  </section>

  <section class="dark:bg-none section-spacing">
    <div class="container-wrapper-xl">
      <h2 class="lg:text-center mb-6">
        <WrGradientText>
          {{ data?.framework_benefits?.heading }}
        </WrGradientText>
      </h2>
      <p
        class="max-w-screen-lg mx-auto lg:text-center lg:text-xl tracking-[0.5px]"
      >
        {{ data?.framework_benefits?.subheading }}
      </p>

      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
        <div v-for="service in data?.framework_benefits?.benefit_items">
          <ServiceValueCard
            :img="service?.image"
            :title="service?.title"
            :description="service?.text"
          />
        </div>
      </div>
    </div>
  </section>

  <section class="dark:bg-none section-spacing">
    <div class="container-wrapper-xl">
      <WrCardCTA
        append-class="max-md:px-4 py-8"
        :description="data?.project_in_mind?.subheading"
        :icon="data?.project_in_mind?.image"
        :title="data?.project_in_mind?.heading"
        :subtitle="data?.project_in_mind?.small_heading"
        :button-text="data?.project_in_mind?.linkText"
      />
    </div>
  </section>

  <section class="dark:bg-none section-spacing">
    <div class="container-wrapper-xl">
      <WrImageAndCardSection :data="data?.partnering" />
      <WrGoogleRating />
    </div>
  </section>

  <section class="dark:bg-none section-spacing">
    <div class="container-wrapper-xl">
      <WrCardCTA
        img-position="left"
        append-class="max-md:px-4 py-8"
        :description="data?.contact_us_bar?.subheading"
        :icon="data?.contact_us_bar?.image"
        :title="data?.contact_us_bar?.heading"
        :subtitle="data?.contact_us_bar?.small_heading"
        :button-text="data?.contact_us_bar?.linkText"
        icon-append-class="true"
      />
    </div>
  </section>

  <section class="dark:bg-none section-spacing">
    <div class="container-wrapper-xl">
      <h2 class="lg:text-center">
        <WrGradientText>
          {{ data.development_process.heading }}
        </WrGradientText>
      </h2>
      <p
        class="max-w-screen-lg mx-auto lg:text-center lg:text-xl tracking-[0.5px] mb-10"
      >
        {{ data.development_process.subheading }}
      </p>
      <WrStepper
        :steps="data.development_process.steps"
        :layout="isMobile ? 'mobile' : 'desktop'"
      />
    </div>
  </section>

  <section class="bg-none section-spacing">
    <div
      class="container-wrapper-xl flex max-lg:flex-col justify-between items-center gap-14 lg:gap-32 lg:*:w-1/2"
    >
      <div>
        <h2 class="text-3xl lg:text-4xl mb-2 max-md:mb-4 md:mb-4">
          <WrGradientText
            :text="data?.contact_us_bar2?.heading"
            append-class="pb-0 lg:pb-2 bg-clip-text bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_-1.57%,_var(--text-gradient-2)_30.72%)]"
          />
        </h2>
        <p class="lg:text-xl tracking-[0.5px]">
          {{ data?.contact_us_bar2?.subheading }}
        </p>
      </div>
      <WrCardCTA
        append-class="lg:py-14 lg:mr-8"
        :icon="data?.contact_us_bar2?.box?.image"
        alt="Development Services"
        :subtitle="data?.contact_us_bar2?.box?.small_heading"
        :title="data?.contact_us_bar2?.box?.heading"
        :description="data?.contact_us_bar2?.box?.subheading"
        buttonText="Let's Connect"
      />
    </div>
  </section>

  <section class="relative z-10 dark:bg-none section-spacing">
    <div class="container-wrapper-xl">
      <h3
        class="lg:text-[44px] text-[31px] font-bold text-center leading-tight relative xl:px-0 lg:px-20"
      >
        <WrGradientText>
          {{ data.technology_stack.heading }}
        </WrGradientText>
      </h3>
      <p class="xl:mt-4 text-base text-center max-md:px-0 !leading-[1.8]">
        {{ data.technology_stack.subheading }}
      </p>
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <CardTechnologyStack
          v-for="stac in data.technology_stack.stacks"
          :img="stac.image"
          :title="stac.title"
          :stacs="stac.list"
        />
      </div>
    </div>
  </section>

  <section class="dark:bg-none section-spacing">
    <div
      class="container-wrapper-xl w-full grid md:grid-cols-2 gap-6 relative z-10"
    >
      <div
        v-if="$colorMode.preference != 'dark'"
        class="size-[322px] absolute top-40 left-10 rounded-full blur-[150px] bg-[linear-gradient(180deg,_rgba(73,2,166,0.2)_0%,_rgba(255,164,235,0.2)_100%)]"
      ></div>
      <div class="max-md:text-center mx-auto max-w-[504px] z-10 relative">
        <div class="text-3xl lg:text-[48px]">
          <WrGradientText
            :text="data?.faq?.heading"
            append-class="py-0 md:py-4 leading-[1]"
          />
        </div>
        <p class="mt-5 pr-4 text-lg tracking-[0.5px]">
          {{ data?.faq?.subheading }}
        </p>
      </div>
      <div class="grow">
        <WrAccordion :Content="data?.faq?.collapses" />
      </div>
    </div>
  </section>


  <!-- Seleted Project Section -->

 <section class="section-spacing dark:bg-none relative z-10 overflow-visible  order-9">
    <div class="container-wrapper-lg w-full relative">
      <div class="hidden  dark:block opacity-30  md:size-[764px] rounded-full 
         bg-[linear-gradient(136.28deg,_rgba(51,4,79,0)_30.43%,_#260049_54.53%,_#36006F_92.37%)] 
         absolute -top-[30%] -left-[67%]">
      </div>

      <div class="hidden  dark:block opacity-50 size-[190px] rounded-full 
         bg-[image:var(--bg-circle-top-light)] 
         absolute -bottom-[15%] right-0 top-0">
      </div>

      <WrSelectedProject />
    </div>
  </section>


  <section class="dark:bg-none py-20">
    <div
      class="max-w-screen-xl px-4 mx-auto flex max-lg:flex-col justify-between gap-14"
    >
      <div class="lg:w-[45%]">
        <h2 class="text-3xl mb-2 max-md:mb-4 md:mb-4">
          <WrGradientText
            :text="data?.hire_us?.heading"
            append-class="py-0 lg:pb-2 bg-clip-text bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_-1.57%,_var(--text-gradient-2)_30.72%)]"
          />
        </h2>
        <p class="text-xl max-md:text-sm tracking-[0.5px]">
          {{ data?.hire_us?.subheading }}
        </p>
      </div>
      <div class="grid sm:grid-cols-2 gap-4">
        <!-- Hourly Basis -->
        <WrCardHiring
          @openModal="openModal"
          v-for="(card, index) in data?.hire_us?.cards"
          :title="card?.type"
          :description="card?.billing"
          :badgeText="card?.duration"
          :offerText="card?.offerText"
          :icon="card?.image"
          :showCounter="card?.showCounter"
          :defaultCount="parseInt(card?.defaultCount)"
          :buttonLabel="card?.buttonLabel"
          :page="{ name: 'Express JS' }"
          :index="index"
        />
      </div>
    </div>
  </section>

  <section class="max-w-screen-xl mx-auto px-4 py-6">
    <h2 class="lg:text-center">
      <WrGradientText>
        {{ data?.other_services?.side_box?.heading }}
      </WrGradientText>
    </h2>
    <p
      class="max-w-screen-md mx-auto lg:text-lg lg:text-center tracking-[0.5px] mb-5 mt-2"
    >
      {{ data?.other_services?.side_box?.subheading }}
    </p>
    <WrFrameworksCarousel />
  </section>

  <section
    class="relative py-10 lg:py-20 overflow-hidden max-w-screen-xl mx-auto px-4 z-20"
  >
    <h2 class="text-center">
      <WrGradientText text="Trusted for excellence and efficiency" />
    </h2>
    <p
      class="text-lg text-[var(--foreground)] mb-10 text-center tracking-[0.5px]"
    >
      Webreinvent is trusted by leading organisations to enhance their
      development and operational efficiency.
    </p>
    <!-- Carousel Section -->

    <WrTestimonialCardCarousel />

    <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-2"
      >View All</WrButton
    >
  </section>
  <WrContactFormPopup
    :isOpen="visible"
    @close="visible = false"
    class="z-30"
    :prefilledContent="formPrefilledData"
  />
</template>
